/**
 * Helpers puros para el flujo de "Tipo de predio" en Captura.
 *
 *  - `decideTipoDocChange`: reacción al click en una card de Tipo de predio.
 *    Reglas (handoff parcelas privadas v2 Fase 1.5):
 *      - `next === current` → 'noop' (mismo tipo, sin acción).
 *      - `current === null` → 'change' (sin tipo previo elegido; el técnico
 *        está empezando o vuelve de "Cargar otro", sin confirm).
 *      - Si hay datos en juego (step > 1, archivos subidos, parcela
 *        seleccionada) → 'confirm' (modal antes de borrar progreso).
 *      - Si no hay nada que perder → 'change' (cambio directo).
 *
 *  - `tipoDocFromParcelaId`: deriva el tipo de predio del prefijo del
 *    parcela_id. RAN (HUM-P####/COR-P####) → 'ejido'; uso común
 *    (HUM-UC####/COR-UC####, mig 0068) → 'ejido' (tenencia ejidal/comunal:
 *    aplica el checklist documental de ejido — acta de asamblea,
 *    comisariado, etc.); privada (PRV-<TENANT>-#####) → 'privado'; otros
 *    (REF-*, undefined, formato desconocido) → undefined (sin auto-sync,
 *    caso defensivo).
 *
 *  - `evaluateParcelaClickGuard`: decide si un click sobre una parcela
 *    está permitido dado el `tipoDoc` actual. Reject solo cuando hay un
 *    tipoDoc activo Y el clicked es de otro tipo. Permite cuando
 *    tipoDoc=null (el auto-sync lo asignará después) o cuando el clicked
 *    no tiene prefijo reconocible.
 *
 * Funciones puras: testeadas sin React.
 */
export type TipoDoc = 'ejido' | 'privado';

// ─────────────────────────────────────────────────────────────────────────
// decideTipoDocChange (Fase 1.5 — click en card de Tipo de predio)
// ─────────────────────────────────────────────────────────────────────────

export interface DecideTipoDocChangeInput {
  currentTipoDoc: TipoDoc | null;
  nextTipoDoc: TipoDoc;
  step: number;
  docFilesCount: number;
  hasParcelaSelected: boolean;
}

export type TipoDocChangeAction = 'noop' | 'change' | 'confirm';

export function decideTipoDocChange(input: DecideTipoDocChangeInput): TipoDocChangeAction {
  if (input.nextTipoDoc === input.currentTipoDoc) return 'noop';
  if (input.currentTipoDoc === null) return 'change';
  const hayDatosEnJuego = input.step > 1
    || input.docFilesCount > 0
    || input.hasParcelaSelected;
  return hayDatosEnJuego ? 'confirm' : 'change';
}

// ─────────────────────────────────────────────────────────────────────────
// tipoDocFromParcelaId — auto-detección del tipo desde el prefijo del ID
// ─────────────────────────────────────────────────────────────────────────

export function tipoDocFromParcelaId(parcelaId: string | undefined): TipoDoc | undefined {
  if (!parcelaId) return undefined;
  if (parcelaId.startsWith('HUM-P') || parcelaId.startsWith('COR-P')) return 'ejido';
  // Uso común (mig 0068): tenencia ejidal — mismo flujo documental que ejido.
  if (parcelaId.startsWith('HUM-UC') || parcelaId.startsWith('COR-UC')) return 'ejido';
  if (parcelaId.startsWith('PRV-')) return 'privado';
  return undefined;
}

// ─────────────────────────────────────────────────────────────────────────
// evaluateParcelaClickGuard — bloquea clicks en parcelas de otro tipo
// ─────────────────────────────────────────────────────────────────────────

export type ParcelaClickGuard =
  | { kind: 'allow' }
  | { kind: 'reject'; reason: string };

export function evaluateParcelaClickGuard(args: {
  currentTipoDoc: TipoDoc | null;
  clickedParcelaId: string;
}): ParcelaClickGuard {
  const detected = tipoDocFromParcelaId(args.clickedParcelaId);
  if (detected === undefined) return { kind: 'allow' };       // REF-*, prefijo desconocido
  if (args.currentTipoDoc === null) return { kind: 'allow' }; // sin tipo previo → auto-sync
  if (args.currentTipoDoc === detected) return { kind: 'allow' };
  return {
    kind: 'reject',
    reason: detected === 'privado'
      ? 'Esta parcela es privada. Cambia el tipo a "Privado" antes de seleccionarla.'
      : 'Esta parcela es ejidal. Cambia el tipo a "Ejidal" antes de seleccionarla.',
  };
}
